import {Trans} from '@lingui/macro';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {BoxedInput} from '../../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {OauthClientModel} from '../../../../models/api/oauthClientModel';
import {OauthClientEditorStateActions} from '../../../editors/oauthClientEditor/useOauthClientEditorState';
import {RuleMultilineParameterCard} from '../../../rules/ruleEditor/ruleMultilineParameterCard';

/*
 * Types.
 */

interface OauthClientRedirectUrlsCardProps {
  redirectUrls: OauthClientModel['redirectUrls'];
  onUpdate: OauthClientEditorStateActions['onUpdateRedirectUrls'];
  isReadOnly: boolean;
}

/*
 * Styles.
 */

const StyledRedirectUrlWrapperDiv = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin: -8px 0;
`;

const StyledRedirectUrlBoxedInput = styled(BoxedInput)`
  flex: 1;
`;

/*
 * Component.
 */

export const OauthClientRedirectUrlsCard: FC<OauthClientRedirectUrlsCardProps> = (props) => {
  const {isReadOnly, onUpdate, redirectUrls} = props;
  const [activeUrls, setActiveUrls] = useState(redirectUrls.length === 0 ? [''] : redirectUrls);

  useEffect(() => {
    onUpdate(activeUrls);
  }, [activeUrls, onUpdate]);

  const onInputChange = (value: string, index: number) => {
    setActiveUrls((urls) =>
      urls.map((url, urlIndex) => {
        if (urlIndex === index) {
          return value;
        }
        return url;
      }),
    );
  };

  return (
    <RuleMultilineParameterCard
      title={<Trans id="application-oauth-client-redirect-urls-title">Redirect URLs</Trans>}
      icon={<Icon name="reply" />}
    >
      {activeUrls.map((redirectUrl, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <StyledRedirectUrlWrapperDiv key={`active-url-${index}`}>
          <StyledRedirectUrlBoxedInput isDisabled={isReadOnly}>
            {(childProps) => (
              <BoxedInputElement
                {...childProps}
                type={BoxedInputElementTypesEnum.TEXT}
                value={redirectUrl}
                onChange={(value) => onInputChange(value, index)}
              />
            )}
          </StyledRedirectUrlBoxedInput>

          {maybeRenderActions(isReadOnly, index, activeUrls.length, setActiveUrls)}
        </StyledRedirectUrlWrapperDiv>
      ))}
    </RuleMultilineParameterCard>
  );
};

/*
 * Helpers.
 */

function maybeRenderActions(
  isReadOnly: boolean,
  index: number,
  activeUrlsLength: number,
  setActiveUrls: HandlerOf<(urls: string[]) => string[]>,
) {
  if (isReadOnly) {
    return null;
  }

  return (
    <>
      {index !== 0 && (
        <IconButton
          iconName="trashSmall"
          label={<Trans id="oauth-client-redirect-url-remove">Remove</Trans>}
          onClick={() =>
            setActiveUrls((urls: ReadonlyArray<string>) => urls.filter((value, i) => i !== index))
          }
        />
      )}

      {index === activeUrlsLength - 1 && (
        <IconButton
          iconName="plusCircleSmall"
          label={<Trans id="oauth-client-redirect-url-add">Add new redirect URL</Trans>}
          onClick={() => setActiveUrls((urls) => [...urls, ''])}
        />
      )}
    </>
  );
}
